import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LangProvider } from "./context/LangContext";
import { PinProvider, usePin } from "./context/PinContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import PinLockScreen from "./components/PinLockScreen";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerForm from "./pages/CustomerForm";
import CustomerDetail from "./pages/CustomerDetail";
import Loans from "./pages/Loans";
import LoanForm from "./pages/LoanForm";
import LoanDetail from "./pages/LoanDetail";
import Collections from "./pages/Collections";
import Settings from "./pages/Settings";
import Pigmy from "./pages/Pigmy";
import PigmyDetail from "./pages/PigmyDetail";
import Investors from "./pages/Investors";
import InvestorDetail from "./pages/InvestorDetail";
import Reports from "./pages/Reports";
import AuditLog from "./pages/AuditLog";
import Reminders from "./pages/Reminders";
import DailySheet from "./pages/DailySheet";
import EmiCalendar from "./pages/EmiCalendar";
import CustomerPortal from "./pages/CustomerPortal";
import PublicHome from "./pages/PublicHome";
import AboutUs from "./pages/AboutUs";
import PublicLoans from "./pages/PublicLoans";
import PublicDailySavings from "./pages/PublicDailySavings";
import PublicInvestment from "./pages/PublicInvestment";
import PublicCareers from "./pages/PublicCareers";
import PublicContact from "./pages/PublicContact";
import CustomerLogin from "./pages/CustomerLogin";
import CustomerRecover from "./pages/CustomerRecover";
import ArchivePage from "./pages/Archive";
import DayClose from "./pages/DayClose";
import SuperAdmin from "./pages/SuperAdmin";
import Enquiries from "./pages/Enquiries";
import ChangePasswordFirst from "./pages/ChangePasswordFirst";
import NotifTapGate, { consumePendingDeepLink } from "./components/NotifTapGate";
import { enableStaffPush, onNotifTap, pushSupported } from "./lib/push";
import { api } from "./lib/api";
import { Toaster } from "./components/ui/sonner";
import { useEffect } from "react";

function AppShell({ children, adminOnly = false, superAdminOnly = false }) {
    return (
        <ProtectedRoute adminOnly={adminOnly} superAdminOnly={superAdminOnly}>
            <PinGate>
                <Layout>{children}</Layout>
            </PinGate>
        </ProtectedRoute>
    );
}

function PinGate({ children }) {
    const pin = usePin();
    const { user } = useAuth();
    if (user && pin.enabled && pin.locked) return <PinLockScreen />;
    return children;
}

/**
 * Watches for successful login + PIN unlock to:
 *  1. Auto-subscribe the user to Web Push (idempotent — no re-prompt).
 *  2. Consume any pending notification deep-link and navigate to it.
 *
 * Also listens for `notif-tap` messages from an already-open SW client and
 * forces re-authentication before navigation.
 */
function NotifCoordinator() {
    const { user } = useAuth();
    const pin = usePin();

    // Auto-subscribe on first authenticated + unlocked render.
    useEffect(() => {
        if (!user) return;
        if (pin.enabled && pin.locked) return;           // wait for unlock
        if (!pushSupported()) return;
        // Fire-and-forget — never disrupt the UI if permission is denied.
        enableStaffPush({ api }).catch(() => {});
    }, [user, pin.enabled, pin.locked]);

    // Consume pending deep-link ONLY after a real re-auth cycle completes.
    // Guarded by pathname === "/" so we never fire while still on
    // /notif-tap, /login or a protected page — those routes are the
    // re-auth surfaces themselves.
    useEffect(() => {
        if (!user) return;
        if (pin.enabled && pin.locked) return;
        if (window.location.pathname !== "/") return;
        const deep = consumePendingDeepLink();
        if (deep && deep !== "/" && !deep.startsWith("/notif-tap")) {
            window.location.replace(deep);
        }
    }, [user, pin.enabled, pin.locked]);

    // Bridge: SW may relay a notification tap into an already-open window.
    useEffect(() => {
        const off = onNotifTap((deep) => {
            try {
                sessionStorage.setItem("lokdhara_notif_deep", deep);
                sessionStorage.setItem("lokdhara_notif_event", "info");
            } catch { /* ignore */ }
            if (pin.enabled) {
                pin.lockNow();
                window.location.replace("/");
            } else {
                // Force full re-auth if no PIN is configured.
                window.location.replace("/login");
            }
        });
        return () => off();
    }, [pin]);

    return null;
}

function HomeRoute() {
    const { user, loading } = useAuth();
    if (loading) return null;
    // Public visitors landing on "/" should see the marketing Home, not the login screen.
    if (!user) return <Navigate to="/home" replace />;
    if (user.role === "employee") return <AppShell><DailySheet /></AppShell>;
    return <AppShell><Dashboard /></AppShell>;
}

/* Conditional /loans: authenticated staff -> admin Loans list; public visitors -> marketing page */
function LoansRoute() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) {
        if (user.role !== "admin") return <Navigate to="/" replace />;
        return <AppShell adminOnly><Loans /></AppShell>;
    }
    return <PublicLoans />;
}

function App() {
    return (
        <ThemeProvider>
            <LangProvider>
                <AuthProvider>
                    <PinProvider>
                        <BrowserRouter>
                        <NotifCoordinator />
                        <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route path="/portal" element={<CustomerPortal />} />
                            <Route path="/notif-tap" element={<NotifTapGate />} />
                            <Route path="/home" element={<PublicHome />} />
                            <Route path="/about-us" element={<AboutUs />} />
                            <Route path="/daily-savings" element={<PublicDailySavings />} />
                            <Route path="/investment" element={<PublicInvestment />} />
                            <Route path="/careers" element={<PublicCareers />} />
                            <Route path="/contact-us" element={<PublicContact />} />
                            <Route path="/customer-login" element={<CustomerLogin />} />
                            <Route path="/customer-recover" element={<CustomerRecover />} />
                            <Route path="/change-password" element={<ProtectedRoute><ChangePasswordFirst /></ProtectedRoute>} />
                            <Route path="/" element={<HomeRoute />} />
                            <Route path="/customers" element={<AppShell><Customers /></AppShell>} />
                            <Route path="/customers/new" element={<AppShell adminOnly><CustomerForm /></AppShell>} />
                            <Route path="/customers/:id/edit" element={<AppShell adminOnly><CustomerForm /></AppShell>} />
                            <Route path="/customers/:id" element={<AppShell><CustomerDetail /></AppShell>} />
                            <Route path="/loans" element={<LoansRoute />} />
                            <Route path="/loans/new" element={<AppShell adminOnly><LoanForm /></AppShell>} />
                            <Route path="/loans/:id/edit" element={<AppShell adminOnly><LoanForm /></AppShell>} />
                            <Route path="/loans/:id" element={<AppShell><LoanDetail /></AppShell>} />
                            <Route path="/collections" element={<AppShell><Collections /></AppShell>} />
                            <Route path="/pigmy" element={<AppShell><Pigmy /></AppShell>} />
                            <Route path="/pigmy/:id" element={<AppShell><PigmyDetail /></AppShell>} />
                            <Route path="/investors" element={<AppShell adminOnly><Investors /></AppShell>} />
                            <Route path="/investors/:id" element={<AppShell adminOnly><InvestorDetail /></AppShell>} />
                            <Route path="/reports" element={<AppShell><Reports /></AppShell>} />
                            <Route path="/reminders" element={<AppShell><Reminders /></AppShell>} />
                            <Route path="/daily-sheet" element={<AppShell><DailySheet /></AppShell>} />
                            <Route path="/day-close" element={<AppShell><DayClose /></AppShell>} />
                            <Route path="/super-admin" element={<AppShell superAdminOnly><SuperAdmin /></AppShell>} />
                            <Route path="/enquiries" element={<AppShell adminOnly><Enquiries /></AppShell>} />
                            <Route path="/calendar" element={<AppShell adminOnly><EmiCalendar /></AppShell>} />
                            <Route path="/audit-log" element={<AppShell adminOnly><AuditLog /></AppShell>} />
                            <Route path="/archive" element={<AppShell adminOnly><ArchivePage /></AppShell>} />
                            <Route path="/settings" element={<AppShell><Settings /></AppShell>} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        <Toaster position="top-right" richColors />
                    </BrowserRouter>
                </PinProvider>
            </AuthProvider>
        </LangProvider>
        </ThemeProvider>
    );
}

export default App;
